'use client';

import { useEffect } from 'react';
import { SWRConfig } from 'swr';
import { ToastProvider } from '@/components/shared/ui/toast';
import { AuthProvider, useAuth } from '@/lib/integrations/supabase';
import { NotificationProvider } from '@/lib/features/notifications';
import { ThemeProvider } from '@/lib/integrations/theme';
import { LanguageProvider, useLanguage } from '@/lib/integrations/language';
import { ClarityProvider } from '@/lib/integrations/clarity';
import { swrConfig } from '@/lib/features/swr';
import { HmrErrorHandler } from '@/components/shared/hmr-error-handler';

/**
 * Syncs language preference from user profile to the language context.
 * This ensures that when a user logs in on a new device, their language
 * preference is restored from the database.
 */
function LanguageSyncer({ children }: { children: React.ReactNode }) {
  const { profile, isLoading: authLoading } = useAuth();
  const { initializeFromProfile } = useLanguage();

  useEffect(() => {
    // When profile loads and has a language preference, sync it
    if (!authLoading && profile?.language) {
      initializeFromProfile(profile.language);
    }
  }, [authLoading, profile?.language, initializeFromProfile]);

  return <>{children}</>;
}

export function Providers({ children }: { children: React.ReactNode }) {
  return (
    <SWRConfig value={swrConfig}>
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <ClarityProvider>
              <LanguageSyncer>
                <NotificationProvider>
                  <ToastProvider>
                    <HmrErrorHandler />
                    {children}
                  </ToastProvider>
                </NotificationProvider>
              </LanguageSyncer>
            </ClarityProvider>
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </SWRConfig>
  );
}
